import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Clock,
  CheckCircle2,
  FileText,
  User,
  Calendar,
  ExternalLink,
  ShieldAlert,
  AlertTriangle,
  CheckSquare,
  Square,
  RefreshCw,
  AlertCircle,
  Edit3,
  FileUp,
  Pill
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import PrescriptionNotesThread from '@/components/prescriptions/PrescriptionNotesThread';
import PrescriptionActivityTimeline from '@/components/prescriptions/PrescriptionActivityTimeline';
import ErpSyncBadge from '@/components/prescriptions/ErpSyncBadge';
import PrescriptionEditModal from '@/components/prescriptions/PrescriptionEditModal';
import PrescriptionDocumentModal from '@/components/prescriptions/PrescriptionDocumentModal';
import PrescriptionMedicineEditModal from '@/components/prescriptions/PrescriptionMedicineEditModal';
import { useAuth } from '@/context/AuthContext';

export const UserPrescriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  let authUser = null;
  try {
    const auth = useAuth();
    authUser = auth?.user;
  } catch (err) {
    // Allows mounting in tests without AuthProvider wrapper
  }
  const user = authUser;

  const [prescription, setPrescription] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedMeds, setCheckedMeds] = useState({});

  // Modals
  const [waitingModalOpen, setWaitingModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [waitingReason, setWaitingReason] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentModeError, setPaymentModeError] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // Editability Modals (Phase 1 & Phase 2)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [medicineEditModalOpen, setMedicineEditModalOpen] = useState(false);

  const fetchPrescription = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/prescriptions/${id}`);
      const data = res.data?.data || res.data;
      setPrescription(data);

      if (data?.patient_id) {
        try {
          const pRes = await api.get(`/api/patients/${data.patient_id}`);
          setPatient(pRes.data);
        } catch {
          // Patient info fallback from prescription
        }
      }
    } catch (err) {
      console.error('Failed to fetch prescription', err);
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to load prescription');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPrescription();
  }, [fetchPrescription]);

  // Toggle medicine preparation checklist
  const toggleMedCheck = (index) => {
    setCheckedMeds(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Start processing
  const handleStartWork = async () => {
    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${id}/status`, { status: 'IN_PROGRESS' });
      if (res.data?.success) {
        toast.success('Started processing prescription');
        fetchPrescription();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start processing');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Resume work
  const handleResumeWork = async () => {
    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${id}/status`, { status: 'IN_PROGRESS' });
      if (res.data?.success) {
        toast.success('Resumed processing prescription');
        fetchPrescription();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resume processing');
    } finally {
      setActionSubmitting(false);
    }
  };

  const navigateBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/user/prescriptions');
    }
  };

  // Mark as waiting
  const handleWaitingSubmit = async (e) => {
    e.preventDefault();
    if (!waitingReason.trim()) return;

    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${id}/status`, {
        status: 'WAITING',
        reason: waitingReason.trim()
      });
      if (res.data?.success) {
        toast.success('Prescription placed on hold');
        setWaitingModalOpen(false);
        setWaitingReason('');
        navigateBack();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set waiting status');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Complete work
  const handleCompleteSubmit = async () => {
    const hasMedicines = (prescription?.medicines?.length || 0) > 0;
    const trimmedMode = (paymentMode || '').trim().toUpperCase();
    if (hasMedicines && !trimmedMode) {
      setPaymentModeError('Please select a payment mode before completing the prescription.');
      return;
    }
    setPaymentModeError('');
    setActionSubmitting(true);
    try {
      const payload = {
        status: 'COMPLETED'
      };
      if (trimmedMode) {
        payload.payment_mode = trimmedMode;
      }
      const res = await api.post(`/api/prescriptions/${id}/status`, payload);
      if (res.data?.success) {
        toast.success(hasMedicines ? 'Prescription fulfilled and completed successfully!' : 'Clinical prescription completed successfully!');
        setCompleteModalOpen(false);
        setPaymentMode('');
        navigateBack();
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.response?.data?.detail || 'Failed to mark as completed';
      toast.error(errMsg);
    } finally {
      setActionSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        Loading prescription workspace...
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="text-center py-16 space-y-3">
        <AlertTriangle className="h-10 w-10 text-amber-600 mx-auto" />
        <h2 className="text-xl font-serif font-bold">Prescription Not Found</h2>
        <p className="text-sm text-muted-foreground">This prescription may not exist or is not assigned to your account.</p>
        <Button variant="outline" onClick={() => navigate('/user/dashboard')}>Return to Dashboard</Button>
      </div>
    );
  }

  const isPending = prescription.workflow_status === 'PENDING';
  const isInProgress = prescription.workflow_status === 'IN_PROGRESS';
  const isWaiting = prescription.workflow_status === 'WAITING';
  const isCompleted = prescription.workflow_status === 'COMPLETED';
  const isCancelled = prescription.workflow_status === 'CANCELLED';
  const isAssignedToMe = Boolean(user?.id && prescription.assigned_user_id === user.id);
  const canEdit = !isCompleted && !isCancelled && isAssignedToMe;

  return (
    <div className="space-y-6 sm:pb-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 14rem)' }}>
      {/* Top Bar with Back Button & Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3.5 sm:gap-4">
        <div className="flex items-start justify-between w-full lg:w-auto gap-3">
          <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 mt-0.5 rounded-lg" onClick={navigateBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
                  Prescription #{prescription.id}
                </h1>
                <Badge variant="outline" className={`text-xs font-medium ${
                  isInProgress ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse' :
                  isWaiting ? 'bg-orange-50 text-orange-700 border-orange-200' :
                  isCompleted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {prescription.workflow_status === 'IN_PROGRESS' ? 'In Progress' :
                   prescription.workflow_status === 'WAITING' ? 'Waiting' :
                   prescription.workflow_status === 'COMPLETED' ? 'Completed' :
                   prescription.workflow_status === 'PENDING' ? 'Pending' : prescription.workflow_status}
                </Badge>
                <ErpSyncBadge
                  status={prescription.erp_sync_status}
                  invoiceNo={prescription.erp_invoice_no}
                />
                {prescription.version && prescription.version > 1 && (
                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                    v{prescription.version}
                  </Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Patient: <strong className="font-medium text-foreground">{prescription.patient_name}</strong>
                {prescription.visit_date && <span> • Visit Date: {prescription.visit_date}</span>}
              </p>
            </div>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchPrescription}
            disabled={loading}
            className="sm:hidden h-9 w-9 shrink-0 ml-1 rounded-lg"
            aria-label="Refresh prescription"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Action Controls Bar (Available on all screen sizes) */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 w-full lg:w-auto mt-1 sm:mt-0 sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPrescription}
            disabled={loading}
            className="hidden sm:inline-flex h-9 gap-1.5 text-xs font-medium"
            aria-label="Refresh prescription"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-emerald-800 border-emerald-300 hover:bg-emerald-50 justify-center"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMedicineEditModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-emerald-800 border-emerald-300 hover:bg-emerald-50 justify-center"
              >
                <Pill className="h-3.5 w-3.5" />
                Edit Medicines
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDocumentModalOpen(true)}
                className="h-9 gap-1.5 text-xs font-medium text-blue-800 border-blue-300 hover:bg-blue-50 justify-center"
              >
                <FileUp className="h-3.5 w-3.5" />
                {prescription.file_url ? 'Replace Doc' : 'Upload Doc'}
              </Button>
            </>
          )}

          {isPending && (
            <Button className="bg-primary text-primary-foreground gap-1.5 h-9 text-xs font-medium justify-center flex-1 sm:flex-initial" onClick={handleStartWork} disabled={actionSubmitting}>
              <Play className="h-3.5 w-3.5" />
              Start Processing
            </Button>
          )}

          {isWaiting && (
            <Button className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5 h-9 text-xs font-medium justify-center flex-1 sm:flex-initial" onClick={handleResumeWork} disabled={actionSubmitting}>
              <Play className="h-3.5 w-3.5" />
              Resume Work
            </Button>
          )}

          {isInProgress && (
            <>
              <Button
                variant="outline"
                className="text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5 h-9 text-xs font-medium justify-center flex-1 sm:flex-initial"
                onClick={() => { setWaitingReason(''); setWaitingModalOpen(true); }}
                disabled={actionSubmitting}
              >
                <Clock className="h-3.5 w-3.5" />
                Put on Hold
              </Button>

              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-9 text-xs font-medium justify-center flex-1 sm:flex-initial"
                onClick={() => setCompleteModalOpen(true)}
                disabled={actionSubmitting}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Mark Completed
              </Button>
            </>
          )}

          {isCompleted && (
            <div className="text-xs text-emerald-700 font-medium flex items-center justify-center gap-1 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200 col-span-2 sm:col-span-1">
              <CheckCircle2 className="h-4 w-4" />
              Fulfillment Complete ({new Date(prescription.completed_at || prescription.updated_at || Date.now()).toLocaleDateString()})
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sticky Bottom Action Bar (< sm) - Docked cleanly above bottom nav bar */}
      {(isPending || isWaiting || isInProgress) && (
        <div
          className="fixed left-0 right-0 z-30 p-3 bg-card/95 backdrop-blur-md border-t border-border sm:hidden shadow-lg"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 3.5rem)' }}
        >
        {isPending && (
          <Button className="w-full h-12 text-base font-semibold bg-primary text-primary-foreground gap-2" onClick={handleStartWork} disabled={actionSubmitting}>
            <Play className="h-5 w-5" />
            Start Processing
          </Button>
        )}

        {isWaiting && (
          <Button className="w-full h-12 text-base font-semibold bg-teal-600 hover:bg-teal-700 text-white gap-2" onClick={handleResumeWork} disabled={actionSubmitting}>
            <Play className="h-5 w-5" />
            Resume Work
          </Button>
        )}

        {isInProgress && (
          <div className="flex items-center gap-2 w-full">
            <Button
              variant="outline"
              className="flex-1 h-12 text-sm font-semibold text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5"
              onClick={() => { setWaitingReason(''); setWaitingModalOpen(true); }}
              disabled={actionSubmitting}
            >
              <Clock className="h-4 w-4" />
              Put on Hold
            </Button>

            <Button
              className="flex-1 h-12 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              onClick={() => setCompleteModalOpen(true)}
              disabled={actionSubmitting}
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </Button>
          </div>
        )}

        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Clinical Prescription Details (Read-only) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Overview Card */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3 border-b border-border/50 py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-serif flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                Patient Demographics
              </CardTitle>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditModalOpen(true)}
                  className="h-7 text-xs gap-1 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-xs">
              <div>
                <span className="text-muted-foreground block text-[11px]">Full Name</span>
                <span className="font-semibold text-foreground text-sm">{prescription.patient_name}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-[11px]">Age / Gender</span>
                <span className="font-medium text-foreground">
                  {patient?.age ? `${patient.age} yrs` : '—'} / <span className="capitalize">{patient?.gender || '—'}</span>
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block text-[11px]">Contact Phone</span>
                <span className="font-medium text-foreground">{patient?.phone || '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-[11px]">Address</span>
                <span className="font-medium text-foreground truncate block">{patient?.address || '—'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Medicines Checklist Card (Clinical Data) */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-serif flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Doctor's Prescription & Preparation Checklist
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Clinical data is read-only. Click items to check them off as you prepare.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMedicineEditModalOpen(true)}
                      className="h-8 gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                    >
                      <Pill className="h-3.5 w-3.5" />
                      Edit Medicines
                    </Button>
                  )}
                  {prescription.medicines && (
                    <Badge variant="outline" className="text-xs">
                      {prescription.medicines.length} Medicine(s)
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {prescription.medicines && prescription.medicines.length > 0 ? (
                <>
                  {/* Mobile Touch Checklist (< md) */}
                  <div className="block md:hidden divide-y divide-border">
                    {prescription.medicines.map((med, idx) => {
                      const isDone = Boolean(checkedMeds[idx]);
                      return (
                        <div
                          key={idx}
                          onClick={() => toggleMedCheck(idx)}
                          className={`p-3.5 flex items-start gap-3 cursor-pointer transition-colors active:bg-muted/50 ${
                            isDone ? 'bg-muted/30 text-muted-foreground' : 'hover:bg-muted/20'
                          }`}
                        >
                          <div className="mt-0.5 flex-shrink-0">
                            {isDone ? (
                              <CheckSquare className="h-6 w-6 text-emerald-600" />
                            ) : (
                              <Square className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-semibold text-sm ${isDone ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {med.name}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
                              {med.dispensed_quantity && (
                                <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded text-[11px] font-medium">
                                  Qty: {med.dispensed_quantity} {med.dispensed_unit || 'units'}
                                </span>
                              )}
                              <span className="bg-muted px-2 py-0.5 rounded text-[11px]">
                                {med.dosage || 'Dosage: —'}
                              </span>
                              <span className="bg-muted px-2 py-0.5 rounded text-[11px]">
                                {med.frequency || 'Freq: —'}
                              </span>
                              <span className="bg-muted px-2 py-0.5 rounded text-[11px]">
                                {med.duration || 'Duration: —'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop Table (md+) */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">Done</TableHead>
                          <TableHead>Medicine Name</TableHead>
                          <TableHead>Dispensed Qty</TableHead>
                          <TableHead>Dosage</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {prescription.medicines.map((med, idx) => {
                          const isDone = Boolean(checkedMeds[idx]);
                          return (
                            <TableRow
                              key={idx}
                              onClick={() => toggleMedCheck(idx)}
                              className={`cursor-pointer transition-colors ${isDone ? 'bg-muted/30 line-through text-muted-foreground' : 'hover:bg-muted/40'}`}
                            >
                              <TableCell className="w-10">
                                {isDone ? (
                                  <CheckSquare className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <Square className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell className="font-semibold text-foreground">
                                {med.name}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {med.dispensed_quantity ? `${med.dispensed_quantity} ${med.dispensed_unit || 'units'}` : '—'}
                              </TableCell>
                              <TableCell>{med.dosage || '—'}</TableCell>
                              <TableCell>{med.frequency || '—'}</TableCell>
                              <TableCell>{med.duration || '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {prescription.file_url && (
                    <div className="p-3 sm:p-6 border-t border-border space-y-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground pb-2 border-b border-border">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          Uploaded Prescription Document
                        </span>
                        <div className="flex items-center gap-2">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1.5 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => setDocumentModalOpen(true)}
                            >
                              <FileUp className="h-3.5 w-3.5" />
                              Replace
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5 text-xs text-primary hover:text-primary"
                            onClick={() => window.open(prescription.file_url, '_blank')}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            View Full Size
                          </Button>
                        </div>
                      </div>
                      <div className="relative overflow-hidden rounded-xl border border-border bg-slate-50/50 dark:bg-slate-900/40 p-2 sm:p-4 flex items-center justify-center">
                        <img
                          src={prescription.file_url}
                          alt="Doctor's Uploaded Prescription"
                          className="w-full max-h-[750px] object-contain rounded-lg shadow-sm"
                          loading="eager"
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : prescription.file_url ? (
                <div className="p-3 sm:p-6 space-y-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground pb-2 border-b border-border">
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      Uploaded Prescription Document
                    </span>
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1.5 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => setDocumentModalOpen(true)}
                        >
                          <FileUp className="h-3.5 w-3.5" />
                          Replace
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1.5 text-xs text-primary hover:text-primary"
                        onClick={() => window.open(prescription.file_url, '_blank')}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View Full Size
                      </Button>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-border bg-slate-50/50 dark:bg-slate-900/40 p-2 sm:p-4 flex items-center justify-center">
                    <img
                      src={prescription.file_url}
                      alt="Doctor's Uploaded Prescription"
                      className="w-full max-h-[750px] object-contain rounded-lg shadow-sm"
                      loading="eager"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center space-y-3">
                  <p className="text-xs text-muted-foreground">
                    No individual medicines listed in this digital prescription (clinical consultation only).
                  </p>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMedicineEditModalOpen(true)}
                      className="text-xs gap-1.5 text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                    >
                      <Pill className="h-3.5 w-3.5" />
                      Add Medicines
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Diet, Lifestyle & Doctor Remarks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                  Dietary Recommendations (Pathya)
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-foreground leading-relaxed">
                {prescription.diet ? (
                  <p className="whitespace-pre-wrap">{prescription.diet}</p>
                ) : (
                  <p className="text-muted-foreground italic">No specific diet restrictions recorded.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                  Lifestyle Guidelines (Vihara)
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-foreground leading-relaxed">
                {prescription.lifestyle ? (
                  <p className="whitespace-pre-wrap">{prescription.lifestyle}</p>
                ) : (
                  <p className="text-muted-foreground italic">No lifestyle guidelines recorded.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {prescription.notes && (
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                  Doctor's Clinical Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-foreground leading-relaxed">
                <p className="whitespace-pre-wrap">{prescription.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column: Communication Notes & Audit Timeline */}
        <div className="space-y-6">
          {/* Notes Thread */}
          <PrescriptionNotesThread
            prescriptionId={prescription.id}
            notes={prescription.notes_list || []}
            onNoteAdded={fetchPrescription}
            readOnly={false}
          />

          {/* Activity Ledger */}
          <PrescriptionActivityTimeline
            activities={prescription.activities || []}
          />
        </div>
      </div>

      {/* MARK AS WAITING MODAL */}
      <Dialog open={waitingModalOpen} onOpenChange={setWaitingModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              Put Prescription on Hold / Waiting
            </DialogTitle>
            <DialogDescription className="text-xs">
              State the reason why processing is paused (e.g. out of stock, clarifying dosage with doctor).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleWaitingSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Reason for Hold *</label>
              <Textarea
                placeholder="Describe what is needed before fulfillment can resume..."
                required
                className="min-h-[90px] text-xs resize-none"
                value={waitingReason}
                onChange={(e) => setWaitingReason(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWaitingModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={actionSubmitting || !waitingReason.trim()}
              >
                {actionSubmitting ? 'Updating...' : 'Confirm Hold'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* COMPLETE CONFIRMATION MODAL */}
      <Dialog
        open={completeModalOpen}
        onOpenChange={(open) => {
          setCompleteModalOpen(open);
          if (!open) {
            setPaymentMode('');
            setPaymentModeError('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              Complete Prescription Fulfillment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Confirm compounding and dispensing for <strong>{prescription.patient_name}</strong>.
            </DialogDescription>
          </DialogHeader>

          {(prescription?.medicines?.length || 0) > 0 ? (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="payment-mode-select" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Payment Mode <span className="text-rose-500">*</span>
                </Label>
                <Select
                  value={paymentMode}
                  onValueChange={(val) => {
                    setPaymentMode(val);
                    if (paymentModeError) setPaymentModeError('');
                  }}
                >
                  <SelectTrigger
                    id="payment-mode-select"
                    data-testid="payment-mode-select"
                    className={`h-11 ${paymentModeError ? 'border-rose-500 ring-rose-500' : ''}`}
                  >
                    <SelectValue placeholder="Select payment mode (Required)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="CARD">Card</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="NET_BANKING">Net Banking</SelectItem>
                  </SelectContent>
                </Select>
                {paymentModeError && (
                  <p className="text-xs text-rose-600 font-medium flex items-center gap-1 mt-1" role="alert" data-testid="payment-mode-error">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    {paymentModeError}
                  </p>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Payment mode is mandatory and will be synchronized with OneSpace ERP to issue the final commercial sale invoice.
              </p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                <div className="font-semibold text-slate-800">Clinical-Only Completion</div>
                <p className="text-slate-600">
                  This prescription contains 0 medicines. Completing it will finalize the clinical consultation without ERP billing or inventory deduction.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setCompleteModalOpen(false);
                setPaymentMode('');
                setPaymentModeError('');
              }}
              disabled={actionSubmitting}
            >
              Back
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleCompleteSubmit}
              disabled={actionSubmitting}
              data-testid="confirm-complete-btn"
            >
              {actionSubmitting ? 'Completing...' : (prescription?.medicines?.length || 0) > 0 ? 'Yes, Confirm Fulfillment' : 'Confirm Clinical Completion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Edit & Document Replacement Modals */}
      <PrescriptionEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        prescription={prescription}
        patient={patient}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
      <PrescriptionDocumentModal
        open={documentModalOpen}
        onOpenChange={setDocumentModalOpen}
        prescription={prescription}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
      <PrescriptionMedicineEditModal
        open={medicineEditModalOpen}
        onOpenChange={setMedicineEditModalOpen}
        prescription={prescription}
        onSuccess={() => {
          fetchPrescription();
        }}
        onConflict={fetchPrescription}
      />
    </div>
  );
};

export default UserPrescriptionDetail;
